import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { getLocalizedText, formatPrice, formatDuration } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SEO, generateServiceSchema } from '../components/SEO';
import { ChevronRight, ChevronLeft, Clock, MapPin, User, Mail, Phone, Globe, Sparkles, X, ZoomIn, Star, ExternalLink, Flag, AlertTriangle } from 'lucide-react';
import SocialShare from '../components/SocialShare';
import ReportButton from '../components/ReportButton';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image Gallery Component
function ImageGallery({ images, gallery, mainImage, serviceName }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Combine all images: main image first, then gallery images
  const allImages = React.useMemo(() => {
    const result = [];
    
    if (mainImage) {
      result.push({ url: mainImage, caption: '' });
    }
    
    if (images && images.length > 0) {
      images.forEach((img) => {
        if (img !== mainImage) {
          result.push({ url: img, caption: '' });
        }
      });
    }
    
    if (gallery && gallery.length > 0) {
      gallery.forEach(item => {
        if (!result.find(i => i.url === item.url)) {
          result.push(item);
        }
      });
    }
    
    return result;
  }, [mainImage, images, gallery]);
  
  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };
  
  const closeLightbox = () => setLightboxOpen(false);
  
  const goNext = React.useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  }, [allImages.length]);
  
  const goPrev = React.useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);
  
  // Handle keyboard navigation - must be before any conditional return
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goNext, goPrev]);
  
  // Return null after all hooks
  if (allImages.length === 0) return null;
  
  return (
    <>
      {/* Gallery Grid */}
      <div className="mb-8">
        {allImages.length === 1 ? (
          // Single image - full width
          <div 
            className="relative h-72 md:h-96 rounded-2xl overflow-hidden cursor-pointer group"
            onClick={() => openLightbox(0)}
          >
            <img 
              src={allImages[0].url} 
              alt={serviceName} 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ) : allImages.length === 2 ? (
          // Two images - side by side
          <div className="grid grid-cols-2 gap-2 h-72 md:h-96">
            {allImages.slice(0, 2).map((img, idx) => (
              <div 
                key={idx}
                className="relative rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(idx)}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </div>
            ))}
          </div>
        ) : (
          // Three or more images - masonry style
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2 h-72 md:h-96">
            {/* Main large image */}
            <div 
              className="col-span-2 row-span-2 relative rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(0)}
            >
              <img src={allImages[0].url} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </div>
            
            {/* Secondary images */}
            {allImages.slice(1, 5).map((img, idx) => (
              <div 
                key={idx}
                className={`relative rounded-xl overflow-hidden cursor-pointer group ${idx === 3 && allImages.length > 5 ? 'relative' : ''}`}
                onClick={() => openLightbox(idx + 1)}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                
                {/* Show remaining count on last visible image */}
                {idx === 3 && allImages.length > 5 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">+{allImages.length - 5}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Thumbnails for mobile */}
        {allImages.length > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2 md:hidden">
            {allImages.map((img, idx) => (
              <div 
                key={idx}
                className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-gold"
                onClick={() => openLightbox(idx)}
                style={{ borderColor: 'transparent' }}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Lightbox - rendered via portal to escape overflow:hidden containers */}
      {lightboxOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-black flex flex-col" 
          onClick={closeLightbox}
        >
          {/* Header with close button - mobile optimized */}
          <div className="flex-shrink-0 flex justify-between items-center p-3 md:p-4">
            <p className="text-white/60 text-sm">
              {currentIndex + 1} / {allImages.length}
            </p>
            <button 
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              onClick={closeLightbox}
            >
              <X className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </button>
          </div>
          
          {/* Main image area - fills available space */}
          <div 
            className="flex-1 flex items-center justify-center relative px-2 md:px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Navigation arrows */}
            {allImages.length > 1 && (
              <>
                <button 
                  className="absolute left-1 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
                  onClick={(e) => { e.stopPropagation(); goPrev(); }}
                >
                  <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </button>
                <button 
                  className="absolute right-1 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
                  onClick={(e) => { e.stopPropagation(); goNext(); }}
                >
                  <ChevronRight className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </button>
              </>
            )}
            
            {/* Image - centered in available space */}
            <img 
              src={allImages[currentIndex].url} 
              alt="" 
              style={{ 
                maxWidth: 'calc(100vw - 2rem)', 
                maxHeight: 'calc(100vh - 120px)',
                objectFit: 'contain'
              }}
              className="rounded-lg" 
            />
          </div>
          
          {/* Caption if exists */}
          {allImages[currentIndex].caption && (
            <div className="flex-shrink-0 px-4 py-2">
              <p className="text-white text-center text-sm md:text-base">
                {allImages[currentIndex].caption}
              </p>
            </div>
          )}
          
          {/* Thumbnails - only if more than 1 image */}
          {allImages.length > 1 && (
            <div className="flex-shrink-0 py-3 px-2 md:px-4">
              <div className="flex gap-2 justify-center overflow-x-auto">
                {allImages.map((img, idx) => (
                  <div 
                    key={idx}
                    className={`w-12 h-12 md:w-14 md:h-14 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer transition-all ${
                      idx === currentIndex ? 'ring-2 ring-white scale-105' : 'opacity-50 hover:opacity-100'
                    }`}
                    onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export default function ServiceDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders } = useAuthStore();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/services/${id}`);
        setService(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const handleContact = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/messages`, {
        recipient_id: service.teacher?.id,
        recipient_type: 'teacher',
        subject: `Consulta sobre ${getLocalizedText(service.title, i18n.language)}`,
        content: message,
        related_service_id: service.id,
      }, { headers: getAuthHeaders() });
      setContactOpen(false);
      setMessage('');
      alert('Mensaje enviado correctamente');
    } catch (e) {
      alert('Error al enviar mensaje');
    }
    setSending(false);
  };

  if (loading) return <div className="text-center py-12">{t('common.loading')}</div>;
  if (!service) return <div className="text-center py-12">Servicio no encontrado</div>;

  const hasServiceImage = service.images && service.images.length > 0;
  const hasTeacherImage = service.teacher?.images && service.teacher.images.length > 0;
  const teacher = service.teacher || {};
  const hasGallery = (service.gallery && service.gallery.length > 0) || (service.images && service.images.length > 0);
  
  const audienceLabels = {
    all: 'Todos', women: 'Mujeres', men: 'Hombres', couples: 'Parejas', singles: 'Singles', lgbtqia: 'LGBTQIA+', lgbtq: 'LGBTQ+'
  };
  
  const serviceTypeLabels = {
    tantric_massage: 'Masaje Tántrico', tantra_coaching: 'Coaching Tántrico',
    couple_session: 'Sesión de Pareja', tantra_therapy: 'Terapia Tántrica',
    tantra_initiation: 'Iniciación Tántrica', yoga: 'Yoga',
    meditation: 'Meditación', breathwork: 'Respiración', bodywork: 'Trabajo Corporal'
  };

  // SEO data
  const serviceTitle = getLocalizedText(service.title, i18n.language);
  const descriptionText = getLocalizedText(service.description, i18n.language);
  const seoDescription = descriptionText ? descriptionText.substring(0, 155) + '...' : `${serviceTitle} - Servicio de ${serviceTypeLabels[service.service_type] || 'tantra'} en ${service.location?.city || 'España'}`;
  const serviceSchema = generateServiceSchema(service, teacher);

  return (
    <>
      <SEO
        title={serviceTitle}
        description={seoDescription}
        image={hasServiceImage ? service.images[0] : undefined}
        url={`/service/${id}`}
        type="website"
        schema={serviceSchema}
      />
      <div className="max-w-5xl mx-auto px-4 py-8 animate-fade-in" data-testid="service-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold transition-colors">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Hero Image or Gallery */}
        {hasGallery ? (
          <div className="p-4 md:p-6">
            <ImageGallery 
              images={service.images} 
              gallery={service.gallery}
              mainImage={service.images?.[0]}
              serviceName={getLocalizedText(service.title, i18n.language)}
            />
          </div>
        ) : (
          <div className="h-64 md:h-80 bg-gradient-to-br from-cream-dark to-cream flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #F5EFE6, #FDFBF7)' }}>
            <Sparkles className="h-24 w-24 text-gold opacity-30" style={{ color: '#D4A574' }} />
          </div>
        )}
        
        <div className="p-6 md:p-8">
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            {service.is_featured && <Badge className="bg-gold text-white" style={{ backgroundColor: '#D4A574' }}>Destacado</Badge>}
            {service.service_type && <Badge variant="outline">{serviceTypeLabels[service.service_type] || service.service_type}</Badge>}
          </div>
          
          {/* Title and Price */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-primary">{getLocalizedText(service.title, i18n.language)}</h1>
            {service.price > 0 && (
              <span className="text-2xl md:text-3xl font-bold text-gold whitespace-nowrap" style={{ color: '#D4A574' }}>
                {formatPrice(service.price, service.currency)}
              </span>
            )}
          </div>

          {/* Teacher Card */}
          <Link to={`/teacher/${teacher.id}`} className="block mb-6">
            <div className="flex items-center gap-4 p-4 bg-cream rounded-xl hover:bg-cream-dark transition-colors" style={{ backgroundColor: '#F8F6F3' }}>
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white overflow-hidden flex-shrink-0 shadow-sm">
                {hasTeacherImage ? (
                  <img src={teacher.images[0]} alt={service.teacher_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-7 w-7 md:h-8 md:w-8 text-gold" style={{ color: '#D4A574' }} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-primary truncate">{service.teacher_name || teacher.name || 'Profesional'}</p>
                <p className="text-sm text-secondary truncate">{teacher.location?.city || 'Ver perfil'}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-secondary flex-shrink-0" />
            </div>
          </Link>

          {/* Google Business Rating */}
          {teacher.google_business_rating?.is_verified && teacher.google_business_rating?.google_rating > 0 && (
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg shadow-sm">
                    <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    <span className="text-2xl font-bold text-gray-800">
                      {teacher.google_business_rating.google_rating.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">
                      {teacher.google_business_rating.google_reviews_count} {t('googleRating.reviews', 'reseñas')}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <img src="https://www.google.com/favicon.ico" alt="Google" className="w-3 h-3" />
                      Google Business
                    </p>
                  </div>
                </div>
                {teacher.google_business_rating.google_maps_url && (
                  <a
                    href={teacher.google_business_rating.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors text-sm font-medium border border-gray-200"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {t('googleRating.verify', 'Verificar en Google')}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Follow-up Session Pricing - Only show if configured */}
          {(service.followup_price > 0 || service.followup_duration_minutes > 0) && (
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-green-800 mb-1">
                    {t('service.followupTitle', '¿Ya eres cliente?')}
                  </h4>
                  <p className="text-sm text-green-700 mb-3">
                    {t('service.followupDesc', 'Precio especial para sesiones sucesivas')}
                  </p>
                  <div className="flex flex-wrap items-center gap-4">
                    {service.followup_price > 0 && (
                      <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-green-100">
                        <p className="text-xs text-green-600 uppercase tracking-wide">{t('service.price', 'Precio')}</p>
                        <p className="text-xl font-bold text-green-700">{formatPrice(service.followup_price, service.currency)}</p>
                      </div>
                    )}
                    {service.followup_duration_minutes > 0 && (
                      <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-green-100">
                        <p className="text-xs text-green-600 uppercase tracking-wide">{t('service.duration', 'Duración')}</p>
                        <p className="text-xl font-bold text-green-700">{formatDuration(service.followup_duration_minutes)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
            {service.duration_minutes > 0 && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Clock className="h-5 w-5 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
                <div className="min-w-0">
                  <p className="text-xs text-secondary">Duración</p>
                  <p className="font-medium text-sm truncate">{formatDuration(service.duration_minutes)}</p>
                </div>
              </div>
            )}
            {service.location?.city && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
                <div className="min-w-0">
                  <p className="text-xs text-secondary">Ubicación</p>
                  <p className="font-medium text-sm truncate">{service.location.city}</p>
                </div>
              </div>
            )}
            {service.location?.postal_code && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
                <div className="min-w-0">
                  <p className="text-xs text-secondary">Código Postal</p>
                  <p className="font-medium text-sm truncate">{service.location.postal_code}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Target Audience */}
          {service.target_audience && service.target_audience.length > 0 && !service.target_audience.includes('all') && (
            <div className="mb-6">
              <h3 className="font-semibold text-primary mb-2">Reservado a</h3>
              <div className="flex flex-wrap gap-2">
                {service.target_audience.map(audience => (
                  <Badge key={audience} variant="outline" className="bg-cream" style={{ backgroundColor: '#F8F6F3' }}>
                    {audienceLabels[audience] || audience}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="prose max-w-none mt-6 mb-8">
            <h3 className="font-serif text-xl font-semibold mb-3">Descripción</h3>
            <p className="text-secondary whitespace-pre-line leading-relaxed">{getLocalizedText(service.description, i18n.language)}</p>
          </div>

          {/* Contact Section */}
          <div className="border-t pt-6">
            <h3 className="font-serif text-xl font-semibold mb-4">Contacto</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {teacher.phone && (
                <a href={`https://wa.me/${teacher.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors" data-testid="contact-whatsapp">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-green-800">WhatsApp</p>
                    <p className="text-sm text-green-600 truncate">{teacher.phone}</p>
                  </div>
                </a>
              )}

              {teacher.phone && (
                <a href={`tel:${teacher.phone}`} className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors" data-testid="contact-phone">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-blue-800">Telefono</p>
                    <p className="text-sm text-blue-600 truncate">{teacher.phone}</p>
                  </div>
                </a>
              )}

              {service.teacher_email && (
                <a href={`mailto:${service.teacher_email}`} className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors" data-testid="contact-email">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-purple-800">Email</p>
                    <p className="text-sm text-purple-600 truncate">{service.teacher_email}</p>
                  </div>
                </a>
              )}

              {teacher.website && (
                <a href={teacher.website} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors" data-testid="contact-website">
                  <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-orange-800">Sito Web</p>
                    <p className="text-sm text-orange-600 truncate">{teacher.website.replace(/^https?:\/\//, '')}</p>
                  </div>
                </a>
              )}
            </div>

            {isAuthenticated && (
              <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full mt-4 bg-gold hover:bg-gold/90 text-white py-3" style={{ backgroundColor: '#D4A574' }} data-testid="send-message-btn">
                    <Mail className="h-5 w-5 mr-2" /> Enviar Mensaje
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Contactar a {service.teacher_name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Textarea placeholder="Escribe tu mensaje..." value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="contact-message" />
                    <Button onClick={handleContact} disabled={!message || sending} className="w-full bg-gold text-white" style={{ backgroundColor: '#D4A574' }} data-testid="submit-message-btn">
                      {sending ? 'Enviando...' : 'Enviar Mensaje'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {!isAuthenticated && (
              <Link to="/login" className="block mt-4">
                <Button className="w-full bg-gold hover:bg-gold/90 text-white py-3" style={{ backgroundColor: '#D4A574' }}>
                  Iniciar sesion para enviar mensaje
                </Button>
              </Link>
            )}
          </div>

          {/* Social Share Section */}
          <div className="border-t pt-6 mt-6">
            <SocialShare 
              url={window.location.href}
              title={getLocalizedText(service.title, i18n.language)}
              description={getLocalizedText(service.description, i18n.language)?.substring(0, 150)}
            />
          </div>

          {/* Report Button */}
          <div className="border-t pt-4 mt-4 flex justify-center">
            <ReportButton 
              contentType="service"
              contentId={service.id}
              contentTitle={getLocalizedText(service.title, i18n.language)}
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
